// おすすめ案件誘導モーダルのCookie定義と開閉制御。
// モーダルはレイアウト（layouts/v2/application.html.slim）が描画し、setupJobOfferGuidanceModal を
// レイアウト共有JSから呼ぶ。Cookie定数は各画面のトリガーJSとも共有し、名称・期限の二重管理を防ぐ。
// Cookie名はサーバー側（app/controllers/concerns/job_offer_guidance_modalable.rb）とも対で定義している。変更時は両方更新すること
export const JOB_OFFER_GUIDANCE_LATER_COOKIE = 'job_offer_guidance-later'
export const JOB_OFFER_GUIDANCE_ETERNAL_CLOSE_COOKIE = 'job_offer_guidance-eternal-close'
export const JOB_OFFER_GUIDANCE_OPEN_AFTER_RELOAD_COOKIE = 'job_offer_guidance-open-after-reload'

// 「あとで」: 5時間 / 「今後表示しない」: 7日（単位: 秒）
export const JOB_OFFER_GUIDANCE_LATER_MAX_AGE = 5 * 60 * 60
export const JOB_OFFER_GUIDANCE_ETERNAL_CLOSE_MAX_AGE = 7 * 24 * 60 * 60

// フラッシュが描画されない場合にモーダルを開く保険タイムアウト
const FLASH_WAIT_TIMEOUT_MS = 1000

export const hasJobOfferGuidanceCookie = (name: string): boolean => {
  return document.cookie.split('; ').some(cookie => cookie.startsWith(`${name}=`))
}

// 遷移先でモーダルを開くためのフラグ（短命Cookie）を立てる。
// サーバーのredirectを通らないReact保存経路（経験スキル等）から、遷移直前に呼ぶ。
// 表示対象セグメント（モーダル要素が描画済み）のときだけ立てる。later/eternal-close による
// 抑止判定は開く側（openJobOfferGuidanceModal）に一本化しており、フラグを立てる側では行わない。
export const requestJobOfferGuidanceModalAfterReload = (): void => {
  if (document.getElementById('job_offer_guidance_modal_status') === null) return

  document.cookie = `${JOB_OFFER_GUIDANCE_OPEN_AFTER_RELOAD_COOKIE}=true;path=/;max-age=60`
}

// 表示対象のフラッシュメッセージ（success/notice/alert/error）が存在するか
const hasDisplayableFlash = (): boolean => {
  const placeholder = document.querySelector('[data-react-flash]') as HTMLElement | null
  if (!placeholder?.dataset.reactFlash) return false
  try {
    const { flash } = JSON.parse(placeholder.dataset.reactFlash)
    return ['success', 'notice', 'alert', 'error'].some(key => flash?.[key])
  } catch {
    return false
  }
}

// モーダルの開閉制御一式（Observerによる開くトリガーの一元化・Cookie抑止・閉じる操作）を初期化する。
// モーダル要素が無いページ（表示対象外）では何もしない。
export const setupJobOfferGuidanceModal = (): void => {
  // 保存成功時のフラグ（サーバー/React保存の双方が立てる）は、遷移直後のページ限りのワンショット。
  // モーダル非描画ページでも通過時に消費（削除）し、後続ページへ持ち越して遅れて開くことを防ぐ
  const openAfterReloadRequested = hasJobOfferGuidanceCookie(JOB_OFFER_GUIDANCE_OPEN_AFTER_RELOAD_COOKIE)
  if (openAfterReloadRequested) {
    document.cookie = `${JOB_OFFER_GUIDANCE_OPEN_AFTER_RELOAD_COOKIE}=;path=/;max-age=0`
  }

  const jobOfferGuidanceModal = document.querySelector<HTMLElement>('.js-job-offer-guidance-modal')
  const jobOfferGuidanceModalStatus = document.getElementById('job_offer_guidance_modal_status')
  if (!jobOfferGuidanceModal || !jobOfferGuidanceModalStatus) return

  const showJobOfferGuidanceModal = () => {
    jobOfferGuidanceModal.hidden = false
  }

  const openJobOfferGuidanceModal = () => {
    if (hasJobOfferGuidanceCookie(JOB_OFFER_GUIDANCE_LATER_COOKIE)) return
    if (hasJobOfferGuidanceCookie(JOB_OFFER_GUIDANCE_ETERNAL_CLOSE_COOKIE)) return
    // フラッシュメッセージが無ければ即時、有ればDOMに描画された直後に開く（遅延を入れずトーストを覆わない）
    if (!hasDisplayableFlash()) {
      showJobOfferGuidanceModal()
      return
    }
    const deadline = Date.now() + FLASH_WAIT_TIMEOUT_MS
    const openWhenFlashVisible = () => {
      if (document.querySelector('.flash-alert') || Date.now() > deadline) {
        showJobOfferGuidanceModal()
        return
      }
      window.requestAnimationFrame(openWhenFlashVisible)
    }
    window.requestAnimationFrame(openWhenFlashVisible)
  }

  const closeJobOfferGuidanceModal = (cookieName: string, maxAge: number) => {
    document.cookie = `${cookieName}=true;path=/;max-age=${maxAge}`
    jobOfferGuidanceModal.hidden = true
  }

  const jobOfferGuidanceObserver = new MutationObserver(() => {
    if (jobOfferGuidanceModalStatus.getAttribute('value') === '') {
      return
    }
    openJobOfferGuidanceModal()
    jobOfferGuidanceModalStatus.setAttribute('value', '')
  })
  jobOfferGuidanceObserver.observe(jobOfferGuidanceModalStatus, { attributes: true, childList: false, subtree: false })

  // 保存成功後のページ遷移時、冒頭で消費したフラグが立っていればモーダルを開く
  if (openAfterReloadRequested) {
    jobOfferGuidanceModalStatus.setAttribute('value', 'open')
  }

  const jobOfferGuidanceLaterButton = document.querySelector('.js-job-offer-guidance-later')
  if (jobOfferGuidanceLaterButton) {
    jobOfferGuidanceLaterButton.addEventListener('click', () => {
      closeJobOfferGuidanceModal(JOB_OFFER_GUIDANCE_LATER_COOKIE, JOB_OFFER_GUIDANCE_LATER_MAX_AGE)
    })
  }

  const jobOfferGuidanceEternalCloseButton = document.querySelector('.js-job-offer-guidance-eternal-close')
  if (jobOfferGuidanceEternalCloseButton) {
    jobOfferGuidanceEternalCloseButton.addEventListener('click', () => {
      closeJobOfferGuidanceModal(JOB_OFFER_GUIDANCE_ETERNAL_CLOSE_COOKIE, JOB_OFFER_GUIDANCE_ETERNAL_CLOSE_MAX_AGE)
    })
  }

  // モーダル外クリックは「あとで」と同じ扱い
  jobOfferGuidanceModal.addEventListener('click', (event) => {
    if (event.target === jobOfferGuidanceModal) {
      closeJobOfferGuidanceModal(JOB_OFFER_GUIDANCE_LATER_COOKIE, JOB_OFFER_GUIDANCE_LATER_MAX_AGE)
    }
  })
}
